/**
 * Audit log — quién hizo qué en el tenant.
 *
 * Captura por cada acción: actor (tipo, email, userId), entidad afectada, cambios diff,
 * IP origen, user agent, geo (país/ciudad estimado por Cloudflare), request_id, timestamp.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, PageHeader, Input } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Shield, Globe, User, Key, MapPin, Filter } from 'lucide-react'

export const Route = createFileRoute('/store/$id/audit')({
  component: AuditPage,
})

interface AuditLog {
  id: string
  tenantId: string
  userId: string | null
  actorType: 'platform_admin' | 'tenant_admin' | 'tenant_member' | 'api_key' | 'customer' | 'guest' | 'system'
  actorEmail: string | null
  actorName: string | null
  apiKeyId: string | null
  action: string
  entityType: string
  entityId: string | null
  changes: Record<string, { from: unknown; to: unknown }> | null
  metadata: Record<string, unknown>
  ipAddress: string | null
  userAgent: string | null
  geoCountry: string | null
  geoCity: string | null
  geoRegion: string | null
  requestId: string | null
  createdAt: string
}

interface AuditResponse {
  data: AuditLog[]
  stats: {
    total: number
    byActorType: Record<string, number>
    byEntity: Record<string, number>
  }
}

function AuditPage() {
  const { activeTenantId } = useAuth()
  const [filterAction, setFilterAction] = useState('')
  const [filterActor, setFilterActor] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', filterAction, filterActor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200' })
      if (filterAction) params.set('action', filterAction)
      if (filterActor) params.set('actorType', filterActor)
      return tenantApi.get<AuditResponse>(`/api/v1/audit-logs?${params}`)
    },
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []
  const stats = data?.stats

  return (
    <>
      <PageHeader title="Auditoría" description="Bitácora de quién hizo qué en este tenant" />
      <div className="p-8 space-y-4">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Eventos totales" value={stats.total} icon={<Shield className="size-4" />} />
            <Kpi label="API keys" value={stats.byActorType.api_key ?? 0} icon={<Key className="size-4" />} color="text-blue-400" />
            <Kpi label="Tenant admin" value={(stats.byActorType.tenant_admin ?? 0) + (stats.byActorType.tenant_member ?? 0)} icon={<User className="size-4" />} color="text-emerald-400" />
            <Kpi label="Platform admin" value={stats.byActorType.platform_admin ?? 0} icon={<Globe className="size-4" />} color="text-purple-400" />
          </div>
        )}

        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-neutral-400">
              <Filter className="size-4" /> Filtros:
            </div>
            <Input
              placeholder="Acción (ej. payment_method, checkout)"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="max-w-xs"
            />
            <select
              value={filterActor}
              onChange={(e) => setFilterActor(e.target.value)}
              className="bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
            >
              <option value="">Todos los actores</option>
              <option value="platform_admin">Platform admin</option>
              <option value="tenant_admin">Tenant admin</option>
              <option value="tenant_member">Tenant member</option>
              <option value="api_key">API key</option>
              <option value="customer">Customer</option>
              <option value="guest">Guest (storefront)</option>
              <option value="system">Sistema</option>
            </select>
          </div>
        </Card>

        {isLoading && <p className="text-sm text-neutral-500">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Shield className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">Sin actividad registrada</p>
            <p className="text-sm text-neutral-500 mt-1">Cuando alguien haga cambios o un API key reciba checkouts, aparecerán acá.</p>
          </Card>
        )}

        {list.length > 0 && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-950 border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Cuándo</th>
                    <th className="text-left px-4 py-3">Quién</th>
                    <th className="text-left px-4 py-3">Acción</th>
                    <th className="text-left px-4 py-3">Origen</th>
                    <th className="text-left px-4 py-3">Detalles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {list.map((log) => <AuditRow key={log.id} log={log} />)}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  )
}

function AuditRow({ log }: { log: AuditLog }) {
  const date = new Date(log.createdAt)
  const dateLabel = date.toLocaleString('es-CO', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })

  const actorLabel = log.actorEmail ?? log.actorName ?? (log.actorType === 'api_key' ? `API key${log.apiKeyId ? ' ' + log.apiKeyId.slice(0, 8) : ''}` : log.actorType)

  const actorBadge = {
    platform_admin: { label: 'Platform admin', variant: 'warning' as const },
    tenant_admin: { label: 'Tenant admin', variant: 'success' as const },
    tenant_member: { label: 'Miembro', variant: 'success' as const },
    api_key: { label: 'API key', variant: 'muted' as const },
    customer: { label: 'Cliente', variant: 'default' as const },
    guest: { label: 'Guest', variant: 'default' as const },
    system: { label: 'Sistema', variant: 'muted' as const },
  }[log.actorType]

  const geoLabel = [log.geoCity, log.geoRegion, log.geoCountry].filter(Boolean).join(', ') || '—'

  return (
    <tr className="hover:bg-neutral-900/50 align-top">
      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap text-xs">{dateLabel}</td>
      <td className="px-4 py-3">
        <div className="space-y-1">
          <Badge variant={actorBadge.variant}>{actorBadge.label}</Badge>
          <p className="text-xs text-neutral-300 font-mono">{actorLabel}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <code className="text-xs px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800">{log.action}</code>
        <p className="text-xs text-neutral-500 mt-1">{log.entityType}{log.entityId ? ` · ${log.entityId.slice(0, 8)}` : ''}</p>
      </td>
      <td className="px-4 py-3 text-xs">
        {log.ipAddress && <p className="font-mono text-neutral-300">{log.ipAddress}</p>}
        <div className="flex items-center gap-1 text-neutral-500 mt-0.5">
          <MapPin className="size-3" />
          <span>{geoLabel}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-neutral-400">
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <details>
            <summary className="cursor-pointer hover:text-white text-neutral-300">
              {Object.keys(log.metadata).length} campos
            </summary>
            <pre className="mt-2 text-[10px] bg-neutral-950 border border-neutral-800 rounded p-2 max-w-md overflow-x-auto">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </details>
        )}
        {log.changes && (
          <details className="mt-1">
            <summary className="cursor-pointer hover:text-white text-neutral-300">Diff</summary>
            <pre className="mt-2 text-[10px] bg-neutral-950 border border-neutral-800 rounded p-2 max-w-md overflow-x-auto">
              {JSON.stringify(log.changes, null, 2)}
            </pre>
          </details>
        )}
      </td>
    </tr>
  )
}

function Kpi({ label, value, color, icon }: { label: string; value: number | string; color?: string; icon?: React.ReactNode }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-xs text-neutral-400 uppercase tracking-wider">
        {icon}
        <span>{label}</span>
      </div>
      <p className={cn('text-3xl font-bold tabular-nums mt-1', color)}>{value}</p>
    </Card>
  )
}
